import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Copy, Eye, EyeOff, Clock, Users, MousePointer,
  BarChart3, Globe, FileText, Calendar, TrendingUp, Smartphone, Monitor,
  Tablet, MapPin, RefreshCw, Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { landingPagesApi } from '@/api/landingPages';
import { apiV1 } from '@/api/client';
import { useAppSettings } from '@/contexts/AppSettingsContext';

const statusColors = {
  draft: 'bg-slate-500',
  published: 'bg-emerald-500',
  archived: 'bg-gray-500',
};

export default function LandingPageDetails() {
  const { id } = useParams();
  const { buildLandingPageUrl } = useAppSettings();
  const [page, setPage] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [dateRange, setDateRange] = useState('30d');

  useEffect(() => {
    if (id) {
      loadPage();
      loadAnalytics();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadAnalytics();
    }
  }, [dateRange]);

  const loadPage = async () => {
    setLoading(true);
    try {
      const response = await landingPagesApi.get(id);
      if (response.success) {
        setPage(response.data);
      } else {
        toast.error('Failed to load page');
      }
    } catch (error) {
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      // Calculate date range
      const end = new Date();
      const start = new Date();
      if (dateRange === '24h') start.setHours(start.getHours() - 24);
      else if (dateRange === '7d') start.setDate(start.getDate() - 7);
      else if (dateRange === '30d') start.setDate(start.getDate() - 30);
      else if (dateRange === '90d') start.setDate(start.getDate() - 90);

      // Fetch analytics from the landing pages API
      const response = await apiV1.get(`/pages/${id}/analytics`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        }
      });

      if (response.data.success) {
        setAnalytics(response.data.data);
      } else {
        // Set default empty analytics
        setAnalytics({
          total_views: 0,
          unique_views: 0,
          form_submissions: 0,
          conversion_rate: null,
          devices: [],
          locations: [],
          daily_views: [],
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Set default empty analytics
      setAnalytics({
        total_views: 0,
        unique_views: 0,
        form_submissions: 0,
        conversion_rate: null,
        devices: [],
        locations: [],
        daily_views: [],
      });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Page not found</h2>
        <Button asChild className="mt-4">
          <Link to="/pages">Back to Pages</Link>
        </Button>
      </div>
    );
  }

  const pageUrl = buildLandingPageUrl(page.slug);

  return (
    <div className="space-y-6" data-testid="landing-page-details">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/pages">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">{page.title}</h1>
            <Badge className={`${statusColors[page.status]} text-white`}>
              {page.status}
            </Badge>
          </div>
          <p className="text-muted-foreground ml-12">{page.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/pages/${page.id}`}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Page
            </Link>
          </Button>
          {page.status === 'published' && (
            <Button variant="outline" asChild>
              <a href={`https://${pageUrl}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Live
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Page Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Page Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">URL</p>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm bg-muted px-2 py-1 rounded truncate max-w-[200px]">
                  {pageUrl}
                </code>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(`https://${pageUrl}`)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium mt-1">{formatDate(page.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Updated</p>
              <p className="font-medium mt-1">{formatDate(page.updated_at)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Sections</p>
              <p className="font-medium mt-1">{page.sections?.length || 0} sections</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Section */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Analytics</h2>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadAnalytics}>
            <RefreshCw className={`h-4 w-4 ${loadingAnalytics ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                {loadingAnalytics ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{(analytics?.total_views || 0).toLocaleString()}</p>
                )}
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Visitors</p>
                {loadingAnalytics ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{(analytics?.unique_views || 0).toLocaleString()}</p>
                )}
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Form Submissions</p>
                {loadingAnalytics ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{(analytics?.form_submissions || 0).toLocaleString()}</p>
                )}
                {/* Scoped submissions inbox -- /submissions?page_id=<id> */}
                <Link
                  to={`/submissions?page_id=${id}`}
                  className="text-xs text-primary hover:underline mt-1 inline-block"
                >
                  View submissions →
                </Link>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                <MousePointer className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                {loadingAnalytics ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <>
                    <p className="text-3xl font-bold" data-testid="conversion-rate">
                      {analytics?.conversion_rate == null
                        ? 'Not available'
                        : `${analytics.conversion_rate.toFixed(1)}%`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics?.conversion_rate == null
                        ? 'No visitors counted in this period'
                        : analytics?.conversion_rate_capped
                          ? 'Capped at 100%: some visitors submitted more than once'
                          : 'Submissions per unique visitor'}
                    </p>
                  </>
                )}
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Daily Views</CardTitle>
              <CardDescription>
                Page views per day (SAST), bots excluded.
                {analytics?.views_tracked_since
                  ? ` Counted since ${new Date(analytics.views_tracked_since).toLocaleDateString()}; earlier views were not recorded.`
                  : ' Views are not being counted yet.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAnalytics ? (
                <Skeleton className="h-48 w-full" />
              ) : analytics?.daily_views?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead className="text-right">Unique</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.daily_views.slice(0, 10).map((day, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell className="text-right font-medium">{day.total}</TableCell>
                        <TableCell className="text-right">{day.unique}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No view data available yet</p>
                  <p className="text-sm">Data will appear once the page receives visitors</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Device Breakdown</CardTitle>
              <CardDescription>Visitors by device type</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAnalytics ? (
                <Skeleton className="h-48 w-full" />
              ) : analytics?.devices?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.devices.map((device, idx) => {
                    const icons = {
                      mobile: Smartphone,
                      desktop: Monitor,
                      tablet: Tablet,
                    };
                    const Icon = icons[device.type?.toLowerCase()] || Monitor;
                    const maxCount = Math.max(...analytics.devices.map(d => d.count));
                    const percentage = maxCount > 0 ? (device.count / maxCount) * 100 : 0;

                    return (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium capitalize">{device.type || 'Unknown'}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {device.count} ({device.percentage?.toFixed(1) || 0}%)
                          </span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Monitor className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No device data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="locations">
          <Card>
            <CardHeader>
              <CardTitle>Top Locations</CardTitle>
              <CardDescription>Visitors by country</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAnalytics ? (
                <Skeleton className="h-48 w-full" />
              ) : analytics && analytics.locations_available === false ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Location lookup is not set up on this server</p>
                </div>
              ) : analytics?.locations?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead className="text-right">Visitors</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.locations.slice(0, 10).map((loc, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {loc.country || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-right font-medium">{loc.count}</TableCell>
                        <TableCell className="text-right">{loc.percentage?.toFixed(1) || 0}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No location data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
